import React, { useEffect, useState, useMemo, useRef } from 'react'

export type AnimationStoryboard = 'thinking' | 'success' | 'documentUpload'

interface GridAnimationProps {
  storyboard: AnimationStoryboard
  className?: string
  onAnimationComplete?: (storyboard: AnimationStoryboard) => void
}

// Pre-computed colors for animations
const SUCCESS_COLORS = [
  '#64B5F6', // Sky blue
  '#4FC3F7', // Light blue
  '#81D4FA', // Lighter blue
  '#FFD54F', // Sunny yellow
  '#FFECB3', // Light yellow
  '#FFF9C4', // Very light yellow
  '#A5D6A7', // Light green
  '#C5E1A5', // Lighter green
]

// Cell shapes for document animation
const CELL_SHAPES = [
  // L-shape
  [
    { x: 0, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: 2 },
    { x: 1, y: 2 },
    { x: 2, y: 2 },
  ],
  // T-shape
  [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 1, y: 1 },
    { x: 1, y: 2 },
  ],
  // Small square
  [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
  ],
  // Cross
  [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
    { x: 2, y: 1 },
    { x: 1, y: 2 },
  ],
]

export const GridAnimation: React.FC<GridAnimationProps> = ({ storyboard, className = '', onAnimationComplete }) => {
  // Hardcoded dimensions with fixed grid size
  const width = 660
  const gridSize = 27 // Grid cell size

  // Calculate height to ensure perfect symmetry (12 rows * gridSize)
  const height = 12 * gridSize + gridSize // 351px (gridSize for rows + gridSize for final line)

  const dotRadius = 2
  const svgWidth = width
  const svgHeight = height

  // State to track animation states for individual dots
  const [dotStates, setDotStates] = useState<
    Array<{
      id: number
      x: number
      y: number
      gridX: number
      gridY: number
      className: string
      fill?: string
      opacity?: number
      animationDelay?: string
      customAnimation?: string
    }>
  >([])

  // State to track grid lines
  const [gridLines, setGridLines] = useState<
    Array<{
      id: number
      x1: number
      y1: number
      x2: number
      y2: number
      isVertical: boolean
    }>
  >([])

  // Reference to animation timeouts for cleanup
  const animationTimeoutsRef = useRef<NodeJS.Timeout[]>([])

  // Track current storyboard for animation transitions
  const currentStoryboardRef = useRef<AnimationStoryboard>(storyboard)

  // Calculate grid dimensions once
  const { numCols, numRows } = useMemo(() => {
    const colCount = Math.floor((svgWidth - gridSize) / gridSize)
    const rowCount = Math.floor((svgHeight - gridSize) / gridSize)
    return { numCols: colCount, numRows: rowCount }
  }, [])

  // Initialize grid and start animation
  useEffect(() => {
    // Clear any existing state or animations
    clearAllTimeouts()

    const newGridLines: typeof gridLines = []
    const newDotStates: typeof dotStates = []

    // Create vertical lines
    for (let i = 1; i <= numCols; i++) {
      const x = i * gridSize
      newGridLines.push({
        id: newGridLines.length,
        x1: x,
        y1: 0,
        x2: x,
        y2: svgHeight,
        isVertical: true,
      })
    }

    // Create horizontal lines
    for (let i = 1; i <= numRows; i++) {
      const y = i * gridSize
      newGridLines.push({
        id: newGridLines.length,
        x1: 0,
        y1: y,
        x2: svgWidth,
        y2: y,
        isVertical: false,
      })
    }

    // Create dots (at grid intersections)
    let dotId = 0
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const x = (col + 1) * gridSize
        const y = (row + 1) * gridSize

        newDotStates.push({
          id: dotId++,
          x,
          y,
          gridX: col,
          gridY: row,
          className: 'grid-dot',
          opacity: 0.2,
          fill: '#555555',
        })
      }
    }

    setGridLines(newGridLines)
    setDotStates(newDotStates)

    // Cleanup on unmount
    return () => {
      clearAllTimeouts()
    }
  }, []) // Only run this once on mount, not when storyboard changes

  // Start animation when dots are initialized and storyboard changes
  useEffect(() => {
    // Only start animation if dots have been initialized
    if (dotStates.length > 0) {
      // Simplify the check - just start the animation if storyboard changes or dots just initialized
      if (storyboard !== currentStoryboardRef.current || animationTimeoutsRef.current.length === 0) {
        // Update current storyboard ref to track what we're showing
        currentStoryboardRef.current = storyboard

        // Start the animation immediately
        startStoryboard(storyboard)
      }
    }

    return () => {
      // Clear any pending animation start timers when this effect is cleaned up
      animationTimeoutsRef.current.forEach((timer) => {
        if (timer) clearTimeout(timer)
      })
    }
  }, [dotStates.length, storyboard]) // Run when dots are initialized or storyboard changes

  // Start a storyboard animation
  const startStoryboard = (nextStoryboard: AnimationStoryboard) => {
    if (dotStates.length === 0) {
      return
    }

    // Stop all running animations by clearing timeouts
    clearAllTimeouts()

    // Do a complete reset of dots first to clear all animations
    setDotStates((prevDots) =>
      prevDots.map((dot) => ({
        ...dot,
        className: 'grid-dot', // Remove any animation class
        fill: '#555555',
        opacity: 0.2,
        animationDelay: undefined, // Clear animation delays
        customAnimation: undefined, // Clear custom animations
        animation: 'none', // Force stop any running animations
      }))
    )

    // Small delay to ensure all previous animations are fully cleared
    setTimeout(() => {
      // Only proceed if the storyboard hasn't changed again
      if (currentStoryboardRef.current !== nextStoryboard) {
        return
      }

      // Prepare fresh dots
      const resetDots = dotStates.map((dot) => ({
        ...dot,
        className: 'grid-dot',
        fill: '#555555',
        opacity: 0.2,
        animationDelay: undefined,
        customAnimation: undefined,
      }))

      // Start the appropriate storyboard
      switch (nextStoryboard) {
        case 'thinking':
          startThinkingAnimation(resetDots)
          break
        case 'success':
          startSuccessAnimation(resetDots)
          break
        case 'documentUpload':
          startDocumentUploadAnimation(resetDots)
          break
      }
    }, 50) // Short delay to ensure reset is processed
  }

  // Thinking animation (random pulsing dots)
  const startThinkingAnimation = (currentDots: typeof dotStates) => {
    // Create more variant delays for dots to make the animation more lively
    const updatedDots = currentDots.map((dot) => {
      // Position-based randomness - use grid position to influence behavior
      // This creates subtle patterns in the randomness
      const positionSeed = (dot.gridX * 7 + dot.gridY * 13) % 17 // Prime number math for pseudo-randomness
      const positionRandom = positionSeed / 17 // 0-1 value based on position

      // Combine multiple random sources for more natural randomness
      const baseRandom = Math.random()
      const tertiaryRandom = Math.random()

      // Chaotic randomness - sometimes invert the random values
      const shouldInvert = tertiaryRandom > 0.7
      const combinedRandom = shouldInvert ? 1 - (baseRandom + positionRandom) / 2 : (baseRandom + positionRandom) / 2

      // -------- Calculate Animation Timing --------

      // Random delay component with natural variation
      let randomDelayMs
      if (positionRandom < 0.3) {
        // 30% fast-starting dots (0-400ms)
        randomDelayMs = positionRandom * 400
      } else if (positionRandom < 0.7) {
        // 40% mid-range dots (400-800ms)
        randomDelayMs = 400 + (positionRandom - 0.3) * 1000
      } else {
        // 30% slow-starting dots (800-1500ms)
        randomDelayMs = 800 + (positionRandom - 0.7) * 2333
      }

      // Convert to CSS delay string
      const randomDelay = `${(randomDelayMs / 1000).toFixed(2)}s`

      // -------- Calculate Visual Appearance --------

      // Determine if this dot will animate at all
      const willAnimate = combinedRandom < 0.75

      // Calculate opacity on a continuous spectrum
      let opacity: number
      if (willAnimate) {
        // Create clustered opacity distribution for more varied grays
        // Use different randomization approach based on the position and another random value
        const opacityCluster = (baseRandom + positionRandom) % 1 // Combined position and random

        if (opacityCluster < 0.3) {
          // 30% low-medium opacity animated dots (0.2-0.5)
          opacity = 0.2 + Math.random() * 0.3
        } else if (opacityCluster < 0.7) {
          // 40% medium opacity animated dots (0.4-0.7)
          opacity = 0.4 + Math.random() * 0.3
        } else if (opacityCluster < 0.9) {
          // 20% high opacity animated dots (0.6-0.9)
          opacity = 0.6 + Math.random() * 0.3
        } else {
          // 10% very high opacity animated dots (0.8-1.0)
          opacity = 0.8 + Math.random() * 0.2
        }
      } else {
        // More variety in non-animated dots too
        // Create subtle background patterns with varying opacity
        const patternSeed = (dot.gridX * 3 + dot.gridY * 5) % 11 // Different pattern seed
        const patternRandom = patternSeed / 11

        // Combine with random for more natural distribution
        opacity = 0.03 + patternRandom * 0.3 + baseRandom * 0.12 // Range 0.03-0.45
      }

      // Determine animation duration with continuous spectrum
      let animationDuration: string | undefined
      let pulseScale: string | undefined

      if (willAnimate) {
        // Continuous range of durations from 1.2s to 3.5s
        // Non-linear distribution - favor medium durations
        const durationBase = 1.2 + tertiaryRandom ** 0.8 * 2.3
        animationDuration = `${durationBase.toFixed(1)}s`

        // Vary the pulse scale too - some dots pulse more dramatically than others
        // Special case: occasionally have a super-dramatic pulse
        const isSpecialPulse = tertiaryRandom > 0.94 // 6% chance
        if (isSpecialPulse) {
          pulseScale = `scale(${(1.8 + Math.random() * 0.4).toFixed(1)})` // 1.8-2.2x scale for special pulses
        } else {
          pulseScale = `scale(${(1.3 + Math.random() * 0.7).toFixed(1)})` // 1.3-2.0x scale normally
        }
      }

      // Create custom animation string if needed
      let customAnimation: string | undefined
      if (willAnimate) {
        if (pulseScale) {
          // Custom pulse with specific scale
          customAnimation = `customPulse${Math.floor(Math.random() * 3) + 1} ${animationDuration} infinite ease-in-out`
        } else {
          // Standard pulse
          customAnimation = `pulse ${animationDuration} infinite ease-in-out`
        }
      }

      return {
        ...dot,
        className: willAnimate ? 'grid-dot thinking-dot' : 'grid-dot',
        fill: '#555555',
        opacity,
        animationDelay: randomDelay,
        customAnimation,
      }
    })

    setDotStates(updatedDots)

    // Instead of refreshing all dots at once, set up continuous rolling updates
    // to avoid the jarring stop-start effect
    setupRollingUpdates()
  }

  // Setup continuous rolling updates for thinking animation
  const setupRollingUpdates = () => {
    if (currentStoryboardRef.current !== 'thinking') return

    // Define parameters for rolling updates
    const minInterval = 500 // Minimum time between updates (ms)
    const maxInterval = 1500 // Maximum time between updates (ms)
    const minDotsPerUpdate = 5 // Minimum dots to update each cycle
    const maxDotsPerUpdate = 15 // Maximum dots to update each cycle

    // Function to update a random subset of dots
    const updateRandomDots = () => {
      // Only continue if we're still in thinking mode
      if (currentStoryboardRef.current !== 'thinking') return

      // Determine how many dots to update in this cycle
      const dotsToUpdate = Math.floor(Math.random() * (maxDotsPerUpdate - minDotsPerUpdate + 1)) + minDotsPerUpdate

      // Generate random indices for dots to update (without duplicates)
      const dotIndices = new Set<number>()
      const dotCount = dotStates.length

      while (dotIndices.size < dotsToUpdate) {
        dotIndices.add(Math.floor(Math.random() * dotCount))
      }

      // Create a set of indices for quick lookup
      const indicesToUpdate = new Set(dotIndices)

      // Update only the selected dots, maintaining the state of others
      setDotStates((prevDots) =>
        prevDots.map((dot, index) => {
          // Skip dots that aren't selected for update
          if (!indicesToUpdate.has(index)) {
            return dot
          }

          // For selected dots, calculate new parameters
          // Similar logic to the original animation but for individual dots
          const baseRandom = Math.random()
          const tertiaryRandom = Math.random()

          // Position-based seed for consistency
          const positionSeed = (dot.gridX * 7 + dot.gridY * 13) % 17
          const positionRandom = positionSeed / 17

          // Combined randomness
          const shouldInvert = tertiaryRandom > 0.7
          const combinedRandom = shouldInvert
            ? 1 - (baseRandom + positionRandom) / 2
            : (baseRandom + positionRandom) / 2

          // Determine if this dot will animate
          const willAnimate = combinedRandom < 0.75

          // Opacity follows same pattern as original but with more variation
          let opacity: number
          if (willAnimate) {
            // Create clustered opacity distribution for more varied grays
            // Use different randomization approach based on the position and another random value
            const opacityCluster = (baseRandom + positionRandom) % 1 // Combined position and random

            if (opacityCluster < 0.3) {
              // 30% low-medium opacity animated dots (0.2-0.5)
              opacity = 0.2 + Math.random() * 0.3
            } else if (opacityCluster < 0.7) {
              // 40% medium opacity animated dots (0.4-0.7)
              opacity = 0.4 + Math.random() * 0.3
            } else if (opacityCluster < 0.9) {
              // 20% high opacity animated dots (0.6-0.9)
              opacity = 0.6 + Math.random() * 0.3
            } else {
              // 10% very high opacity animated dots (0.8-1.0)
              opacity = 0.8 + Math.random() * 0.2
            }
          } else {
            // More variety in non-animated dots too
            // Create subtle background patterns with varying opacity
            const patternSeed = (dot.gridX * 3 + dot.gridY * 5) % 11 // Different pattern seed
            const patternRandom = patternSeed / 11

            // Combine with random for more natural distribution
            opacity = 0.03 + patternRandom * 0.3 + baseRandom * 0.12 // Range 0.03-0.45
          }

          // Animation parameters
          let animationDuration: string | undefined
          let customAnimation: string | undefined

          if (willAnimate) {
            const durationBase = 1.2 + tertiaryRandom ** 0.8 * 2.3
            animationDuration = `${durationBase.toFixed(1)}s`

            const pulseType = Math.floor(Math.random() * 3) + 1
            customAnimation = `customPulse${pulseType} ${animationDuration} infinite ease-in-out`
          }

          // Random delay for smooth transition
          const randomDelayMs = Math.random() * 1000
          const randomDelay = `${(randomDelayMs / 1000).toFixed(2)}s`

          return {
            ...dot,
            className: willAnimate ? 'grid-dot thinking-dot' : 'grid-dot',
            fill: '#555555',
            opacity,
            animationDelay: randomDelay,
            customAnimation,
          }
        })
      )

      // Schedule the next update with a random interval
      const nextInterval = Math.floor(Math.random() * (maxInterval - minInterval + 1)) + minInterval

      const nextTimer = setTimeout(updateRandomDots, nextInterval)
      animationTimeoutsRef.current.push(nextTimer)
    }

    // Start the rolling updates
    const initialTimer = setTimeout(updateRandomDots, 1000)
    animationTimeoutsRef.current.push(initialTimer)
  }

  // Success animation (ripple effect)
  const startSuccessAnimation = (_currentDots: typeof dotStates) => {
    // Calculate center of grid
    const centerX = Math.floor(numCols / 2)
    const centerY = Math.floor(numRows / 2)

    // Calculate max distance for ripples
    const maxDist = Math.sqrt(numCols ** 2 + numRows ** 2) / 2
    const numRings = 12

    // Create ripple effect
    for (let ring = 0; ring < numRings; ring++) {
      const targetRadius = (ring + 1) * (maxDist / numRings)
      const delay = ring * 100 // ms between rings

      animationTimeoutsRef.current.push(
        setTimeout(() => {
          if (currentStoryboardRef.current !== 'success') return

          setDotStates((prevDots) => {
            return prevDots.map((dot) => {
              // Calculate distance from center
              const distX = dot.gridX - centerX
              const distY = dot.gridY - centerY
              const distance = Math.sqrt(distX * distX + distY * distY)

              // Check if this dot is in the current ring
              const tolerance = maxDist / (numRings * 1.5)
              if (Math.abs(distance - targetRadius) <= tolerance) {
                // Pick a random color for this dot
                const color = SUCCESS_COLORS[Math.floor(Math.random() * SUCCESS_COLORS.length)]

                return {
                  ...dot,
                  className: 'grid-dot sparkle-dot',
                  fill: color,
                  opacity: 1, // Ensure full opacity for better color visibility
                  customAnimation: 'sparkle 0.8s forwards',
                }
              }
              return dot
            })
          })
        }, delay)
      )
    }

    // After all rings complete, notify that success animation is done
    animationTimeoutsRef.current.push(
      setTimeout(
        () => {
          if (currentStoryboardRef.current === 'success') {
            // Notify parent component that animation completed
            if (onAnimationComplete) {
              onAnimationComplete('success')
            }
          }
        },
        numRings * 100 + 1200
      )
    )
  }

  // Document upload animation
  const startDocumentUploadAnimation = (_currentDots: typeof dotStates) => {
    // Document dimensions (in grid units)
    const docWidth = 6
    const docHeight = 9

    // Position document (centered horizontally)
    const docLeft = Math.floor((numCols - docWidth) / 2)
    const docRight = docLeft + docWidth

    // Center document between 2nd row and 2nd-to-last row
    // With 12 rows, we position it to start at row 2
    const docTop = 1 // Start at 2nd row (index 1)
    const docBottom = docTop + docHeight

    // First animate document appearance
    setDotStates((prevDots) => {
      return prevDots.map((dot) => {
        // Check if this dot is part of the document
        if (dot.gridX >= docLeft && dot.gridX <= docRight && dot.gridY >= docTop && dot.gridY <= docBottom) {
          // Determine if it's a border dot
          const isBorder =
            dot.gridX === docLeft || dot.gridX === docRight || dot.gridY === docTop || dot.gridY === docBottom

          if (isBorder) {
            // Calculate delay based on position on border
            let normalizedPos = 0
            if (dot.gridY === docTop) {
              normalizedPos = (dot.gridX - docLeft) / docWidth
            } else if (dot.gridY === docBottom) {
              normalizedPos = 1 - (dot.gridX - docLeft) / docWidth
            } else if (dot.gridX === docLeft) {
              normalizedPos = (dot.gridY - docTop) / docHeight
            } else {
              normalizedPos = 1 - (dot.gridY - docTop) / docHeight
            }

            return {
              ...dot,
              fill: '#4285F4',
              opacity: 0,
              customAnimation: 'documentAppear 0.5s forwards',
              animationDelay: `${normalizedPos * 500}ms`,
            }
          }
          // Inner dots appear with delay based on distance from border
          const relX = dot.gridX - docLeft
          const relY = dot.gridY - docTop
          const distFromBorder = Math.min(relX, docWidth - relX - 1, relY, docHeight - relY - 1)

          return {
            ...dot,
            fill: '#A0C3FF',
            opacity: 0,
            customAnimation: 'documentInnerAppear 0.5s forwards',
            animationDelay: `${600 + distFromBorder * 100}ms`,
          }
        }

        return dot
      })
    })

    // Create data cells that animate outward
    const createDataCell = () => {
      try {
        // Choose random position within document
        const cellX = Math.floor(Math.random() * (docWidth - 3)) + docLeft + 1
        const cellY = Math.floor(Math.random() * (docHeight - 3)) + docTop + 1

        // Pick random cell shape
        const cellShape = CELL_SHAPES[Math.floor(Math.random() * CELL_SHAPES.length)]

        // Use alternate animation randomly
        const useAltAnimation = Math.random() > 0.5

        // Calculate center of shape for delay
        const centerX = Math.floor(cellShape.reduce((sum, pt) => sum + pt.x, 0) / cellShape.length)
        const centerY = Math.floor(cellShape.reduce((sum, pt) => sum + pt.y, 0) / cellShape.length)

        // Animate each dot in the cell
        cellShape.forEach((offset) => {
          const targetGridX = cellX + offset.x
          const targetGridY = cellY + offset.y

          // Calculate distance from center for delay
          const distance = Math.sqrt((offset.x - centerX) ** 2 + (offset.y - centerY) ** 2)

          const delay = distance * 80
          const _duration = 1800 + Math.random() * 600

          animationTimeoutsRef.current.push(
            setTimeout(() => {
              if (currentStoryboardRef.current !== 'documentUpload') return

              setDotStates((prevDots) => {
                return prevDots.map((dot) => {
                  if (dot.gridX === targetGridX && dot.gridY === targetGridY) {
                    if (useAltAnimation) {
                      return {
                        ...dot,
                        customAnimation: 'cellGrowAlt 3s forwards',
                      }
                    }
                    return {
                      ...dot,
                      customAnimation: 'cellGrow 3s forwards',
                    }
                  }
                  return dot
                })
              })
            }, delay)
          )
        })
      } catch (e) {
        // Error handling silently
      }
    }

    // Schedule data cells to animate
    const cellCount = 25
    let cellsCreated = 0
    const animationStartTime = Date.now()
    const minAnimationDuration = 10000

    const generateCells = () => {
      // Create 1-3 cells at a time
      const groupSize = Math.floor(Math.random() * 3) + 1

      for (let i = 0; i < groupSize; i++) {
        createDataCell()
      }

      cellsCreated++

      // Calculate time elapsed
      const timeElapsed = Date.now() - animationStartTime

      // Continue if we need more cells or haven't reached min duration
      if (cellsCreated < cellCount || timeElapsed < minAnimationDuration) {
        const nextDelay = Math.random() * 250 + 300
        animationTimeoutsRef.current.push(setTimeout(generateCells, nextDelay))
      } else {
        // Animation completed, notify after delay
        animationTimeoutsRef.current.push(
          setTimeout(() => {
            if (currentStoryboardRef.current === 'documentUpload' && onAnimationComplete) {
              onAnimationComplete('documentUpload')
            }
          }, 1500)
        )
      }
    }

    // Start generating cells after document appears
    animationTimeoutsRef.current.push(setTimeout(generateCells, 1200))
  }

  // Clear all timeouts (for cleanup)
  const clearAllTimeouts = () => {
    if (animationTimeoutsRef.current.length > 0) {
      animationTimeoutsRef.current.forEach((timeout) => {
        clearTimeout(timeout)
      })
      animationTimeoutsRef.current = []
    }
  }

  return (
    <div className={`mx-auto max-w-full ${className}`}>
      <style>{`
        /* Multiple pulse animations with varying intensity */
        @keyframes pulse {
          0%   { transform: scale(1);   opacity: 0.4; }
          50%  { transform: scale(2.0); opacity: 1; }
          100% { transform: scale(1);   opacity: 0.4; }
        }
        
        @keyframes customPulse1 {
          0%   { transform: scale(1);   opacity: 0.3; }
          50%  { transform: scale(1.5); opacity: 0.9; }
          100% { transform: scale(1);   opacity: 0.3; }
        }
        
        @keyframes customPulse2 {
          0%   { transform: scale(1);   opacity: 0.4; }
          50%  { transform: scale(1.8); opacity: 1; }
          100% { transform: scale(1);   opacity: 0.4; }
        }
        
        @keyframes customPulse3 {
          0%   { transform: scale(1);   opacity: 0.5; }
          50%  { transform: scale(2.2); opacity: 1; }
          100% { transform: scale(1);   opacity: 0.5; }
        }
        
        /* Other existing animations */
        @keyframes sparkle {
          0% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(2); opacity: 1; }
          100% { transform: scale(1); opacity: 0.5; fill: #555555; } /* Return to gray at end */
        }
        
        @keyframes cellGrow {
          0%   { transform: scale(1) rotate(0deg); opacity: 0.7; }
          30%  { transform: scale(1.5) rotate(0deg); opacity: 1; }
          100% { transform: translateX(400px) translateY(50px) scale(1.2) rotate(20deg); opacity: 0; }
        }
        
        @keyframes cellGrowAlt {
          0%   { transform: scale(0.9) rotate(-5deg); opacity: 0.5; fill: #555555; }
          25%  { transform: scale(1.4) rotate(0deg); opacity: 0.9; fill: #ffd700; }
          50%  { transform: scale(1.6) rotate(5deg); opacity: 1; fill: #ffa500; }
          100% { transform: translateX(450px) translateY(30px) scale(1.1) rotate(10deg); opacity: 0; }
        }
        
        @keyframes documentAppear {
          0%   { opacity: 0.2; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1.5); }
        }
        
        @keyframes documentInnerAppear {
          0%   { opacity: 0.2; transform: scale(0.9); }
          100% { opacity: 0.8; transform: scale(1.3); }
        }
        
        /* Base styles for dots and grid */
        .grid-line {
          stroke: #dbdbdb9c;
          stroke-width: 1;
        }
        
        .grid-dot {
          fill: #555555;
          transform-box: fill-box;
          transform-origin: center;
          transition: fill 0.3s ease, transform 0.3s ease, opacity 0.3s ease;
        }
        
        .sparkle-dot {
          /* Override transition for sparkle dots to preserve color */
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        
        .thinking-dot {
          animation: pulse 2s infinite ease-in-out;
          animation-play-state: running !important;
          will-change: transform, opacity;
        }
        
        /* Fix for Firefox/Safari */
        circle {
          vector-effect: non-scaling-stroke;
        }
      `}</style>
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        xmlns='http://www.w3.org/2000/svg'
        width={width}
        height={height}
        className='w-full h-auto'
      >
        {/* Background */}
        <rect width={svgWidth} height={svgHeight} className='fill-[#F9F9F9]' />

        {/* Grid lines */}
        <g className='grid-lines'>
          {gridLines.map((line) => (
            <line key={line.id} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} className='grid-line' />
          ))}
        </g>

        {/* Grid dots */}
        <g className='grid-dots'>
          {dotStates.map((dot) => {
            // Handle animation styles properly to avoid conflicts
            let animationStyle = {}

            // If there's a custom animation, use it as the primary animation
            if (dot.customAnimation) {
              // For sparkle animation with colored dots, let the animation handle the full transition
              animationStyle = { animation: dot.customAnimation }
            }
            // If there's only an animation delay, apply it to the class-based animation
            else if (dot.animationDelay) {
              animationStyle = { animationDelay: dot.animationDelay }
            }

            return (
              <circle
                key={dot.id}
                cx={dot.x}
                cy={dot.y}
                r={dotRadius}
                className={dot.className}
                style={{
                  ...animationStyle,
                  fill: dot.fill,
                  opacity: dot.opacity,
                }}
              />
            )
          })}
        </g>
      </svg>
    </div>
  )
}
